import { useState } from 'react';
import { useApi } from '../lib/useApi';
import { qs } from '../lib/api';
import { money, pct, num, MONTH_FULL, today } from '../lib/format';
import { Loader, colorAt } from '../ui/common';
import { Donut, Legend, IncomeExpenseBars, TrendLine } from '../ui/Charts';

const VIEWS = [
  { key: 'monthly', label: 'Monthly Report' },
  { key: 'annual', label: 'Annual Report' },
  { key: 'five', label: '5-Year Trend' },
  { key: 'compare', label: 'Range Comparison' },
  { key: 'custom', label: 'Custom Range' },
];

export default function Dashboards({ config }) {
  const [view, setView] = useState('monthly');
  const currency = config.currency || '₹';
  return (
    <div>
      {/* Segment Selector Buttons */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        {VIEWS.map((v) => (
          <button
            key={v.key}
            type="button"
            style={{
              padding: '10px 18px',
              borderRadius: 10,
              border: view === v.key ? 'none' : '1px solid #cbd5e1',
              background: view === v.key ? '#224eae' : '#ffffff',
              color: view === v.key ? '#ffffff' : '#475569',
              fontWeight: 700,
              cursor: 'pointer',
              boxShadow: view === v.key ? '0 4px 12px rgba(5,150,105,0.25)' : 'none',
              transition: 'all 0.2s ease',
            }}
            onClick={() => setView(v.key)}
          >
            {view === v.key ? '✓ ' : ''}
            {v.label}
          </button>
        ))}
      </div>

      {view === 'monthly' && <Monthly config={config} currency={currency} />}
      {view === 'annual' && <Annual config={config} currency={currency} />}
      {view === 'five' && <FiveYear config={config} currency={currency} />}
      {view === 'compare' && <Compare currency={currency} />}
      {view === 'custom' && <Custom currency={currency} />}
    </div>
  );
}

// --- Shared Summary Stats Cards Grid ---
function SummaryStats({ s, currency, goal }) {
  return (
    <>
      <div className="cm-ref-stats-row">
        <div className="cm-ref-stat-card">
          <div className="cm-ref-stat-label">TOTAL INCOME</div>
          <div className="cm-ref-stat-val" style={{ color: 'var(--brand)' }}>{money(s.income, currency)}</div>
        </div>

        <div className="cm-ref-stat-card">
          <div className="cm-ref-stat-label">TOTAL EXPENSES</div>
          <div className="cm-ref-stat-val" style={{ color: 'var(--ink)' }}>{money(s.expenses, currency)}</div>
        </div>

        <div className="cm-ref-stat-card">
          <div className="cm-ref-stat-label">NET PROFIT / LOSS</div>
          <div className="cm-ref-stat-val" style={{ color: s.profit >= 0 ? '#224eae' : '#0f172a' }}>{money(s.profit, currency)}</div>
        </div>
        <div className="cm-ref-stat-card">
          <div className="cm-ref-stat-label">PROFIT MARGIN</div>
          <div className="cm-ref-stat-val" style={{ color: 'var(--brand)' }}>{pct(s.margin)}</div>
        </div>

        {s.partnerSalary > 0 && (
          <>
            <div className="cm-ref-stat-card" style={{ background: 'var(--bg)', borderColor: 'var(--line)' }}>
              <div className="cm-ref-stat-label" style={{ color: 'var(--muted)' }}>PARTNER DRAWINGS / SALARY</div>
              <div className="cm-ref-stat-val" style={{ color: 'var(--ink)' }}>{money(s.partnerSalary, currency)}</div>
            </div>

            <div className="cm-ref-stat-card" style={{ background: 'var(--chip-bg)', borderColor: '#dbeafe' }}>
              <div className="cm-ref-stat-label" style={{ color: 'var(--brand)' }}>NET PROFIT (AFTER SALARY)</div>
              <div className="cm-ref-stat-val" style={{ color: 'var(--brand)' }}>{money(s.netProfitAfterPartner, currency)}</div>
            </div>
          </>
        )}
        <div className="cm-ref-stat-card">
          <div className="cm-ref-stat-label">GOAL STATUS</div>
          <div className="cm-ref-stat-val" style={{ fontSize: '1.2rem', color: 'var(--muted)' }}>
            {goal && goal.goal > 0 ? `${pct(goal.progress)} Achieved` : 'N/A'}
          </div>
        </div>
      </div>

      {goal && goal.goal > 0 && (
        <div className="cm-form-section-card" style={{ background: 'var(--chip-bg)', borderColor: '#dbeafe' }}>
          <div className="cm-form-section-title" style={{ color: 'var(--brand)' }}>Profit Goal Progress Bar</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800, color: 'var(--ink)', marginBottom: 8 }}>
            <span>Target Goal: {money(goal.goal, currency)}</span>
            <span>{pct(goal.progress)}</span>
          </div>
          <div className="bar-track" style={{ height: 10, background: '#cbd5e1', borderRadius: 10, overflow: 'hidden' }}>
            <div className="bar-fill" style={{ height: '100%', background: '#224eae', width: `${Math.max(0, Math.min(100, goal.progress || 0))}%` }} />
          </div>
          <div style={{ marginTop: 8, fontSize: '0.85rem', color: 'var(--brand)', fontWeight: 700 }}>
            {goal.amountToGoal > 0 ? `${money(goal.amountToGoal, currency)} remaining to reach profit goal` : 'Profit goal fully achieved 🎉'}
          </div>
        </div>
      )}
    </>
  );
}

function donutData(summary) {
  return (summary || []).map((c, i) => ({ label: c.category, value: c.net, color: colorAt(i) }));
}

function DonutCard({ title, summary }) {
  const d = donutData(summary);
  return (
    <div className="cm-form-section-card">
      <div className="cm-form-section-title">{title}</div>
      <div style={{ display: 'flex', justifyContent: 'center', margin: '14px 0' }}><Donut data={d} /></div>
      <Legend data={d} />
    </div>
  );
}

function CatTable({ title, rows, currency }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div className="cm-table-card" style={{ marginBottom: 20 }}>
      <div style={{ padding: '16px 20px', fontWeight: 800, fontSize: '0.95rem', color: 'var(--ink)', borderBottom: '1px solid #e2e8f0' }}>
        {title} ({rows.length} Categories)
      </div>
      <table className="cm-table">
        <thead>
          <tr>
            <th>Category Name</th>
            <th>Net Amount</th>
            <th>Tax</th>
            <th>Total</th>
            <th>Share %</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.category}>
              <td style={{ fontWeight: 800, color: 'var(--ink)' }}>{r.category}</td>
              <td style={{ fontWeight: 700 }}>{num(r.net)}</td>
              <td>{num(r.tax)}</td>
              <td style={{ fontWeight: 800, color: 'var(--brand)' }}>{num(r.total)}</td>
              <td><span className="chip" style={{ background: 'var(--chip-bg)', color: 'var(--brand)', fontWeight: 700 }}>{pct(r.percentage)}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function YearPicker({ value, onChange }) {
  const now = new Date().getFullYear();
  const years = [];
  for (let y = now + 1; y >= now - 6; y--) years.push(y);
  return (
    <select value={value} onChange={(e) => onChange(Number(e.target.value))}>
      {years.map((y) => <option key={y} value={y}>{y}</option>)}
    </select>
  );
}

// --- Monthly ---
function Monthly({ config, currency }) {
  const start = config.startYear || new Date().getFullYear();
  const [year, setYear] = useState(start);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const { data, loading, error } = useApi(`/api/dashboard/monthly${qs({ year, month })}`);
  return (
    <div>
      <div className="cm-form-section-card">
        <div className="cm-grid-2">
          <div className="cm-field-group">
            <label>Select Month</label>
            <select value={month} onChange={(e) => setMonth(Number(e.target.value))}>
              {MONTH_FULL.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
          </div>
          <div className="cm-field-group">
            <label>Select Year</label>
            <YearPicker value={year} onChange={setYear} />
          </div>
        </div>
      </div>

      {error && <div className="err" style={{ marginBottom: 16 }}>{error}</div>}
      {loading || !data ? <Loader /> : (
        <>
          <SummaryStats s={data.summary} currency={currency} goal={data.goal} />
          <div className="cm-grid-2" style={{ marginTop: 20 }}>
            <DonutCard title="Income Breakdown by Category" summary={data.incomeSummary} />
            <DonutCard title="Expense Breakdown by Category" summary={data.expenseSummary} />
          </div>
          <CatTable title="Income Categories Breakdown" rows={data.incomeSummary} currency={currency} />
          <CatTable title="Expense Categories Breakdown" rows={data.expenseSummary} currency={currency} />

          {data.partnerSummary && data.partnerSummary.length > 0 && (
            <>
              <div className="cm-grid-2" style={{ marginTop: 20 }}>
                <div />
                <DonutCard title="Partner Drawings Distribution" summary={data.partnerSummary} />
              </div>
              <CatTable title="Partner Drawings Breakdown" rows={data.partnerSummary} currency={currency} />
            </>
          )}
        </>
      )}
    </div>
  );
}

// --- Annual ---
function Annual({ config, currency }) {
  const [year, setYear] = useState(config.startYear || new Date().getFullYear());
  const { data, loading, error } = useApi(`/api/dashboard/annual${qs({ year })}`);
  return (
    <div>
      <div className="cm-form-section-card">
        <div className="cm-field-group">
          <label>Select Financial Year</label>
          <YearPicker value={year} onChange={setYear} />
        </div>
      </div>

      {error && <div className="err" style={{ marginBottom: 16 }}>{error}</div>}
      {loading || !data ? <Loader /> : (
        <>
          <SummaryStats s={data.summary} currency={currency} goal={data.goal} />
          <div className="cm-form-section-card" style={{ marginTop: 20 }}>
            <div className="cm-form-section-title">Income Growth vs Prior Year</div>
            <div style={{ fontSize: '1.8rem', fontWeight: 800, color: data.incomeGrowth >= 0 ? '#224eae' : '#0f172a' }}>
              {pct(data.incomeGrowth)}
            </div>
          </div>
          <div className="cm-form-section-card">
            <div className="cm-form-section-title">Monthly Income &amp; Expense Chart</div>
            <IncomeExpenseBars series={data.monthly.map((m) => ({ label: m.month, income: m.income, expense: m.expenses }))} currency={currency} />
          </div>
          <div className="cm-form-section-card">
            <div className="cm-form-section-title">Annual Profit Trend</div>
            <TrendLine points={data.monthly.map((m) => ({ label: m.month, value: m.profit }))} />
          </div>
          <div className="cm-grid-2">
            <DonutCard title="Income Distribution" summary={data.incomeSummary} />
            <DonutCard title="Expense Distribution" summary={data.expenseSummary} />
          </div>
          <CatTable title="Top Income Categories" rows={data.topIncome} currency={currency} />
          <CatTable title="Top Expense Categories" rows={data.topExpense} currency={currency} />

          {data.partnerSummary && data.partnerSummary.length > 0 && (
            <>
              <div className="cm-grid-2" style={{ marginTop: 20 }}>
                <div />
                <DonutCard title="Partner Drawings Distribution" summary={data.partnerSummary} />
              </div>
              <CatTable title="Partner Drawings Breakdown" rows={data.partnerSummary} currency={currency} />
            </>
          )}
        </>
      )}
    </div>
  );
}

// --- Five-Year ---
function FiveYear({ config, currency }) {
  const start = config.startYear || new Date().getFullYear();
  const years = [0, 1, 2, 3, 4].map((i) => start + i);
  const { data, loading, error } = useApi(`/api/dashboard/five-year${qs({ years: years.join(',') })}`);
  return (
    <div>
      {error && <div className="err" style={{ marginBottom: 16 }}>{error}</div>}
      {loading || !data ? <Loader /> : (
        <>
          <div className="cm-table-card" style={{ marginBottom: 20 }}>
            <div style={{ padding: '16px 20px', fontWeight: 800, fontSize: '0.95rem', color: 'var(--ink)', borderBottom: '1px solid #e2e8f0' }}>
              5-Year Overview ({years[0]}–{years[4]})
            </div>
            <table className="cm-table">
              <thead>
                <tr><th>Year</th><th>Total Income</th><th>Total Expense</th><th>Net Profit</th><th>Goal %</th></tr>
              </thead>
              <tbody>
                {data.years.map((y) => (
                  <tr key={y.year}>
                    <td style={{ fontWeight: 800 }}>{y.year}</td>
                    <td style={{ fontWeight: 700, color: 'var(--brand)' }}>{num(y.summary.income)}</td>
                    <td style={{ fontWeight: 700, color: 'var(--ink)' }}>{num(y.summary.expenses)}</td>
                    <td style={{ fontWeight: 800, color: y.summary.profit >= 0 ? '#224eae' : '#0f172a' }}>{num(y.summary.profit)}</td>
                    <td><span className="chip" style={{ background: 'var(--chip-bg)', color: 'var(--brand)', fontWeight: 700 }}>{pct(y.goal.progress)}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="cm-form-section-card">
            <div className="cm-form-section-title">5-Year Income &amp; Expense Comparison</div>
            <IncomeExpenseBars series={data.years.map((y) => ({ label: y.year, income: y.summary.income, expense: y.summary.expenses }))} currency={currency} />
          </div>
          <div className="cm-form-section-card">
            <div className="cm-form-section-title">5-Year Profit Trend</div>
            <TrendLine points={data.years.map((y) => ({ label: String(y.year), value: y.summary.profit }))} />
          </div>
          <div className="cm-grid-2">
            <DonutCard title="5-Year Income Distribution" summary={data.incomeDistribution} />
            <DonutCard title="5-Year Expense Distribution" summary={data.expenseDistribution} />
          </div>
        </>
      )}
    </div>
  );
}

// --- Comparison (3 ranges) ---
function Compare({ currency }) {
  const y = new Date().getFullYear();
  const [ranges, setRanges] = useState([
    { from: `${y}-01-01`, to: `${y}-01-31` },
    { from: `${y}-02-01`, to: `${y}-02-28` },
    { from: `${y}-03-01`, to: `${y}-03-31` },
  ]);
  const [applied, setApplied] = useState(ranges);
  const rangeStr = applied.map((r) => `${r.from}:${r.to}`).join(',');
  const { data, loading, error } = useApi(`/api/dashboard/comparison${qs({ ranges: rangeStr })}`);
  const setR = (i, k) => (e) => setRanges((p) => p.map((r, j) => (j === i ? { ...r, [k]: e.target.value } : r)));
  return (
    <div>
      <div className="cm-form-section-card">
        <div className="cm-form-section-title">Compare 3 Custom Date Ranges</div>
        {ranges.map((r, i) => (
          <div className="cm-grid-2" key={i} style={{ marginBottom: 12 }}>
            <div className="cm-field-group">
              <label>Range {i + 1} From</label>
              <input type="date" value={r.from} onChange={setR(i, 'from')} />
            </div>
            <div className="cm-field-group">
              <label>Range {i + 1} To</label>
              <input type="date" value={r.to} onChange={setR(i, 'to')} />
            </div>
          </div>
        ))}
        <button type="button" className="cm-btn-emerald" onClick={() => setApplied(ranges)}>
          Run Range Comparison
        </button>
      </div>

      {error && <div className="err" style={{ marginBottom: 16 }}>{error}</div>}
      {loading || !data ? <Loader /> : data.ranges.map((r, i) => (
        <div className="cm-form-section-card" key={i}>
          <div className="cm-form-section-title">Range {i + 1}: {r.from} → {r.to}</div>
          <div className="cm-ref-stats-row" style={{ marginBottom: 0 }}>
            <div className="cm-ref-stat-card">
              <div className="cm-ref-stat-label">INCOME</div>
              <div className="cm-ref-stat-val" style={{ color: 'var(--brand)' }}>{money(r.summary.income, currency)}</div>
            </div>
            <div className="cm-ref-stat-card">
              <div className="cm-ref-stat-label">EXPENSES</div>
              <div className="cm-ref-stat-val" style={{ color: 'var(--ink)' }}>{money(r.summary.expenses, currency)}</div>
            </div>
            <div className="cm-ref-stat-card">
              <div className="cm-ref-stat-label">PROFIT</div>
              <div className="cm-ref-stat-val" style={{ color: r.summary.profit >= 0 ? '#224eae' : '#0f172a' }}>{money(r.summary.profit, currency)}</div>
            </div>
            <div className="cm-ref-stat-card">
              <div className="cm-ref-stat-label">MARGIN</div>
              <div className="cm-ref-stat-val" style={{ color: 'var(--brand)' }}>{pct(r.summary.margin)}</div>
            </div>
            {r.summary.partnerSalary > 0 && (
              <>
                <div className="cm-ref-stat-card" style={{ background: 'var(--bg)', borderColor: 'var(--line)' }}>
                  <div className="cm-ref-stat-label" style={{ color: 'var(--muted)' }}>PARTNER DRAWINGS</div>
                  <div className="cm-ref-stat-val" style={{ color: 'var(--ink)' }}>{money(r.summary.partnerSalary, currency)}</div>
                </div>
                <div className="cm-ref-stat-card" style={{ background: 'var(--chip-bg)', borderColor: '#dbeafe' }}>
                  <div className="cm-ref-stat-label" style={{ color: 'var(--brand)' }}>NET PROFIT (AFTER SALARY)</div>
                  <div className="cm-ref-stat-val" style={{ color: 'var(--brand)' }}>{money(r.summary.netProfitAfterPartner, currency)}</div>
                </div>
              </>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// --- Custom Range ---
function Custom({ currency }) {
  const y = new Date().getFullYear();
  const [range, setRange] = useState({ from: `${y}-01-01`, to: today() });
  const [applied, setApplied] = useState(range);
  const { data, loading, error } = useApi(`/api/dashboard/custom${qs(applied)}`);
  return (
    <div>
      <div className="cm-form-section-card">
        <div className="cm-grid-2">
          <div className="cm-field-group">
            <label>From Date</label>
            <input type="date" value={range.from} onChange={(e) => setRange((p) => ({ ...p, from: e.target.value }))} />
          </div>
          <div className="cm-field-group">
            <label>To Date</label>
            <input type="date" value={range.to} onChange={(e) => setRange((p) => ({ ...p, to: e.target.value }))} />
          </div>
        </div>
        <button type="button" className="cm-btn-emerald" onClick={() => setApplied(range)}>
          Apply Custom Filter
        </button>
      </div>

      {error && <div className="err" style={{ marginBottom: 16 }}>{error}</div>}
      {loading || !data ? <Loader /> : (
        <>
          <SummaryStats s={data.summary} currency={currency} />
          <CatTable title="Custom Income Summary" rows={data.incomeSummary} currency={currency} />
          <CatTable title="Custom Expense Summary" rows={data.expenseSummary} currency={currency} />

          {data.partnerSummary && data.partnerSummary.length > 0 && (
            <>
              <div className="cm-grid-2" style={{ marginTop: 20 }}>
                <div />
                <DonutCard title="Partner Drawings Distribution" summary={data.partnerSummary} />
              </div>
              <CatTable title="Custom Partner Drawings Summary" rows={data.partnerSummary} currency={currency} />
            </>
          )}
        </>
      )}
    </div>
  );
}
